import { useMemo } from 'react'

import { useModals, useNumericParams, useSetState } from '@domain-group/react-hooks'

import { ModalContext } from '@domain-group/suit-theme'

import PageContext from '@contexts/pageContext'

import handleCallbackAction from '@functions/handleCallbackAction'
import type { HandleCallbackActionParams } from '@functions/handleCallbackAction'

import type { ShowCampaignPortalSyncDetailsModalPayload } from '@hooks/useCampaignPortalSync'

import CampaignPortalSyncDetailsModal from '@modals/CampaignPortalSyncDetailsModal'
import CreateOrEditImageModal from '@modals/CreateOrEditImageModal'
import ManageCampaignTestStatusModal from '@modals/ManageCampaignTestStatusModal'
import MediaReviewerModal from '@modals/MediaReviewerModal'
import ShareProjectModal from '@modals/ShareProjectModal'

import type { CampaignModel, ProviderModel, ImageModel } from '@models/types'

import PageContent from './components/PageContent'

const defaultState = {
  showCreateOrEditImageModal: false,
  showCampaignPortalSyncDetailsModal: false,
  showManageCampaignTestStatusModal: false,
  showMediaReviewerModal: false,
  showShareProjectModal: false,
}

type ModalName = 'CampaignPortalSyncDetailsModal'

type SetState = (state: Partial<typeof defaultState>) => void

const callbacks = (component: ModalName, setState: SetState) => {
  const componentCallbacks = {
    CreateOrEditImageModal: {
      closeModal: () => setState({ showCreateOrEditImageModal: false }),
      deleteImage: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
      setCoverImage: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
    },
    CampaignPortalSyncDetailsModal: {
      closeModal: () => setState({ showCampaignPortalSyncDetailsModal: false }),
      fetchLog: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
    },
    ManageCampaignTestStatusModal: {
      closeModal: () => setState({ showManageCampaignTestStatusModal: false }),
      updateCampaign: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
    },
    MediaReviewerModal: {
      closeModal: () => setState({ showMediaReviewerModal: false }),
    },
    ShareProjectModal: {
      closeModal: () => setState({ showShareProjectModal: false }),
      shareProject: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
    },
  }

  return componentCallbacks[component] || {}
}

const Details = () => {
  const [state, setState] = useSetState(defaultState)
  const {
    showCreateOrEditImageModal,
    showCampaignPortalSyncDetailsModal,
    showManageCampaignTestStatusModal,
    showMediaReviewerModal,
    showShareProjectModal,
  } = state

  const { campaignId } = useNumericParams()

  const modalContext = useModals({ setState })
  const {
    callbacks: { setModalData },
  } = modalContext

  const pageContext = useMemo(() => ({
    callbacks: {
      showCreateOrEditImageModal: (payload: { image: ImageModel }) => {
        setModalData('CreateOrEditImageModal', payload)
        setState({ showCreateOrEditImageModal: true })
      },
      showCampaignPortalSyncDetailsModal: (payload: ShowCampaignPortalSyncDetailsModalPayload) => {
        setModalData('CampaignPortalSyncDetailsModal', payload)
        setState({ showCampaignPortalSyncDetailsModal: true })
      },
      showManageCampaignTestStatusModal: (payload: { campaign: CampaignModel }) => {
        setModalData('ManageCampaignTestStatusModal', payload)
        setState({ showManageCampaignTestStatusModal: true })
      },
      showMediaReviewerModal: (payload: { campaign: CampaignModel, provider: ProviderModel }) => {
        setModalData('MediaReviewerModal', payload)
        setState({ showMediaReviewerModal: true })
      },
      showShareProjectModal: (payload: { campaign: CampaignModel, provider: ProviderModel }) => {
        setModalData('ShareProjectModal', payload)
        setState({ showShareProjectModal: true })
      },
      updateCampaign: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
    },
  }), [])

  return (
    <PageContext.Provider value={pageContext}>
      <ModalContext.Provider value={modalContext}>
        <PageContent campaignId={campaignId} />

        <CampaignPortalSyncDetailsModal
          // @ts-expect-error TS2322
          callbacks={callbacks('CampaignPortalSyncDetailsModal', setState)}
          requestOptions={{ include_campaign_portal_sync_file: true }}
          showModal={showCampaignPortalSyncDetailsModal}
        />

        <CreateOrEditImageModal
          // @ts-expect-error TS2345
          callbacks={callbacks('CreateOrEditImageModal', setState)}
          showModal={showCreateOrEditImageModal}
        />

        <ManageCampaignTestStatusModal
          // @ts-expect-error TS2741
          callbacks={callbacks('ManageCampaignTestStatusModal', setState)}
          showModal={showManageCampaignTestStatusModal}
        />

        <MediaReviewerModal
          // @ts-expect-error TS2345
          callbacks={callbacks('MediaReviewerModal', setState)}
          showModal={showMediaReviewerModal}
        />

        <ShareProjectModal
          // @ts-expect-error TS2741
          callbacks={callbacks('ShareProjectModal', setState)}
          showModal={showShareProjectModal}
        />
      </ModalContext.Provider>
    </PageContext.Provider>
  )
}

export default Details
